import { skipToken } from "@reduxjs/toolkit/query";
import { createContext, useEffect, useMemo, useState } from "react";
import { useGetMedicationsPerProductQuery } from "src/api/services/medicationsApi";
import { usePrescription } from "../PrescriptionProvider";
import { Medication } from "src/api/types";
import { useGetMedicationForPatientQuery } from "src/api/services/medicationTitrationsApi";
import { isTruthy } from "src/utils/isTruthy";
import { useTitration } from "../TitrationProvider/useTitration";

type ContextType = {
  supplyId: number | null;
  setSupplyId: React.Dispatch<React.SetStateAction<number | null>>;
  durationInDays: number | null;
  dosages: string[];
  setDosages: React.Dispatch<React.SetStateAction<string[]>>;
  medications: Medication[];
  medication: Medication | null;
  selectedMedications: (number | null)[];
  setSelectedMedications: React.Dispatch<
    React.SetStateAction<(number | null)[]>
  >;
  quantity: number | null;
  setQuantity: React.Dispatch<React.SetStateAction<number | null>>;
  totalPills: number | null;
};

export const PrescribeContext = createContext<ContextType>({
  dosages: [],
  supplyId: null,
  medications: [],
  selectedMedications: [null],
  setSelectedMedications: () => {},
  setSupplyId: () => {},
  setDosages: () => {},
  durationInDays: null,
  medication: null,
  quantity: null,
  setQuantity: () => {},
  totalPills: null,
});

interface Props {
  children: React.ReactNode;
  region: string | null;
}

const PrescribeProvider = ({ children, region }: Props) => {
  const productId = usePrescription((p) => p.product_id);
  const productSlug = usePrescription((p) => p.product.slug);
  const productCategory = usePrescription((p) => p.product_category.name);
  const isGlp1 = usePrescription((p) => p.product.is_glp1);

  const medicationQuantity = usePrescription((p) => {
    if (p.dispense_quantity == null) return null;
    const quantity = Number(p.dispense_quantity);

    return quantity > 0 ? quantity : null;
  });

  const form = usePrescription((p) => p.metadata?.["form"]);
  const daysSupply = usePrescription((p) => p.days_supply);
  const patientId = usePrescription((p) => p.patient_id);
  const [supplyId, setSupplyId] = useState<number | null>(null);
  const [selectedMedications, setSelectedMedications] = useState<
    (number | null)[]
  >([null]);
  const [quantity, setQuantity] = useState<number | null>(medicationQuantity);
  const [dosages, setDosages] = useState<string[]>([]);

  const getMedicationsArgs = useMemo(() => {
    if (productId == null || region == null) return skipToken;
    return {
      product_id: productId,
      state: region,
      form,
      active: true,
      pharmacy_status: true,
    };
  }, [productId, region, form]);

  const { data: medications = [] } =
    useGetMedicationsPerProductQuery(getMedicationsArgs);
  const titration = useTitration();

  const { data: wlMedication } = useGetMedicationForPatientQuery(
    productCategory === "Weight Loss" && isGlp1
      ? {
          patient_id: patientId,
          product_slug: productSlug,
          state: region,
        }
      : skipToken,
  );

  const medication = useMemo(() => {
    if (wlMedication != null) {
      return wlMedication;
    }

    return medications.find((m) => m.id === selectedMedications[0]) ?? null;
  }, [medications, wlMedication, selectedMedications]);

  const dispenseQuantity = useMemo(() => {
    if (["topical", "dropper"].includes(form || "")) {
      return daysSupply === 150 ? 2 : 1;
    }

    if (productSlug === "ed-ot") {
      return Number(medicationQuantity);
    }

    // if (medication == null) return 0;

    // if (medication?.form === "oral") {
    //   const totalPills = dosages
    //     .filter(isTruthy)
    //     .reduce((acc, dosage) => (acc += parseFloat(dosage)), 0);
    //   const multiplier = titration != null ? 30 : (daysSupply ?? 1);

    //   return totalPills * multiplier;
    // }

    return daysSupply;
  }, [daysSupply, form, medicationQuantity, productSlug]);

  const totalPills = useMemo(() => {
    if (!["db-ot", "mt"].includes(productSlug)) return null;

    const total = dosages
      .filter(isTruthy)
      .reduce((acc, dosage) => (acc += parseFloat(dosage)), 0);
    const multiplier = titration != null ? 30 : (daysSupply ?? 1);

    return total * multiplier;
  }, [daysSupply, dosages, productSlug, titration]);

  const value = useMemo(
    () => ({
      durationInDays: daysSupply,
      selectedMedications,
      dosages,
      setDosages,
      setSelectedMedications,
      supplyId,
      setSupplyId,
      medications,
      medication,
      quantity,
      setQuantity,
      totalPills,
    }),
    [
      daysSupply,
      selectedMedications,
      dosages,
      supplyId,
      medications,
      medication,
      quantity,
      totalPills,
    ],
  );

  useEffect(() => {
    if (wlMedication != null) {
      const medications = new Array(wlMedication.vial_quantity ?? 1).fill(
        wlMedication.id,
      );
      setSelectedMedications(medications);
    }
  }, [wlMedication]);

  useEffect(() => {
    if (daysSupply != null && daysSupply > 0) {
      if (productSlug === "bc") {
        setQuantity(daysSupply);
        setDosages([""]);
        return;
      }

      if (
        [
          "fhl-ot",
          "ed-ot",
          "amh-ot",
          "fhl",
          "b-ot",
          "bb-ot",
          "sl-ot",
          "mhl-ot",
          "os-ot",
        ].includes(productSlug)
      ) {
        setQuantity(dispenseQuantity);
        setDosages([""]);
        return;
      }

      // if (["db-ot"].includes(productSlug)) {
      //   setQuantity(totalPills);
      //   setDosages([""]);
      //   return;
      // }

      setDosages(Array(Math.round(daysSupply / 30)).fill(""));
    }
  }, [daysSupply, dispenseQuantity, productSlug]);

  return (
    <PrescribeContext.Provider value={value}>
      {children}
    </PrescribeContext.Provider>
  );
};

export default PrescribeProvider;
